import { useContext, useEffect, useState } from 'react';
import { MPosContext } from 'utils/context';
import * as Styles from './styles';
import { getFloatBalanceByPartnerId } from './apis';
import { useViewport } from 'utils/responsive';
import { Dropdown } from '@acko-tech/building-blocks.ui.dropdown';
import SummaryCard from './SummaryCard';
import MobileSummaryCard from './MobileSummaryCard';
import { getFloatBalanceSummaryCardConfig } from './helpers';

type ProductOption = {
    value: string;
    displayValue: string;
};

type WalletProduct = {
    product_id: string;
    user_id: string;
    name: string;
};

type LedgerTransactionsHeaderProps = {
    startDate: Date;
    endDate: Date;
    productsList: WalletProduct[];
    selectedProduct: WalletProduct | null;
    setSelectedProduct: (product: WalletProduct) => void;
};

function LedgerTransactionsHeader({
    startDate,
    endDate,
    productsList,
    selectedProduct,
    setSelectedProduct,
}: LedgerTransactionsHeaderProps) {
    const { partnerId, permissions } = useContext(MPosContext);
    const { isMobile } = useViewport();
    const [floatBalance, setFloatBalance] = useState(0);
    const [isFloatBalanceLoading, setIsFloatBalanceLoading] = useState(false);

    const productOptions: ProductOption[] = productsList.map((product) => ({
        value: product.product_id,
        displayValue: product.name,
    }));
    const selectedProductOption = selectedProduct
        ? {
              value: selectedProduct.product_id,
              displayValue: selectedProduct.name,
          }
        : null;

    useEffect(() => {
        const fetchHeaderData = async () => {
            if (!partnerId || !selectedProduct) return;
            setIsFloatBalanceLoading(true);
            const balanceResponse = await getFloatBalanceByPartnerId(
                partnerId,
                permissions.group,
                selectedProduct.user_id,
                selectedProduct.product_id
            );

            if (balanceResponse.status && balanceResponse.data) {
                setFloatBalance(Number(balanceResponse.data) || 0);
            }
            setIsFloatBalanceLoading(false);
        };

        fetchHeaderData();
    }, [partnerId, permissions.group, selectedProduct]);

    const summaryCardConfig = getFloatBalanceSummaryCardConfig(
        floatBalance,
        isFloatBalanceLoading
    );

    return (
        <Styles.HeaderCard>
            <Styles.DropdownRow>
                <Styles.DealerLabel>
                    Search By Wallet Owner Name
                </Styles.DealerLabel>
                <Dropdown
                    className="userDropdown"
                    value={selectedProductOption}
                    label={'Select wallet owner'}
                    options={productOptions}
                    onChange={(selected) => {
                        const product = productsList.find(
                            (item) => item.product_id === selected.value
                        );
                        if (product) {
                            setSelectedProduct(product);
                        }
                    }}
                />
            </Styles.DropdownRow>
            <>
                {isMobile ? (
                    <MobileSummaryCard
                        summaryCardConfig={summaryCardConfig}
                        startDate={startDate}
                        endDate={endDate}
                    />
                ) : (
                    <Styles.SummaryRow>
                        {summaryCardConfig.map((config) => (
                            <SummaryCard
                                key={config.label}
                                config={config}
                                startDate={startDate}
                                endDate={endDate}
                            />
                        ))}
                    </Styles.SummaryRow>
                )}
                <Styles.DataSyncHint>
                    Note: Data Refreshes Every Hour. Reload The Page To See The
                    Latest Numbers.
                </Styles.DataSyncHint>
            </>
        </Styles.HeaderCard>
    );
}

export default LedgerTransactionsHeader;
